import { Button } from '@material-ui/core';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { VEHICLE_STATUSES } from 'constants';
import { useVehicleEditing } from 'context/isAnotherUserEditingContext';
import { useIsOnline } from 'hooks';
import { useCurrentUser } from 'hooks/selectors';
import { useHideOnScroll } from 'hooks/useHideOnScroll';
import { useOpenFinalReview } from 'pages/ListerVehicleUploadV2/hooks/useOpenFinalReview';
import { useMemo } from 'react';
import { useField, useFormState } from 'react-final-form';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useGetActiveAuctionsQuery } from 'services/api/live-auction';
import { useActivateVehicleMutation, useGetVehicleByIdQuery } from 'services/api/upload-vehicle';
import { Section, SectionNotes } from 'services/api/vehicle-sections/types';
import styled from 'styled-components';

export const ActivateButtonUI = styled(Button)<{ withMargin?: boolean }>`
  border-radius: 6px;
  text-transform: none;
  font-weight: 500;
  font-size: 16px;
  line-height: 18.75px;
  text-align: center;
  color: #fff;
  width: 100%;
  height: 44px;
  margin-top: ${props => (props.withMargin ? 'auto' : 0)};
`;

const FinalReviewButton = styled.div<{ bg: string }>`
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  line-heigh: 100%;
  color: #fff;
  width: 115px;
  height: 44px;
  display: flex;
  flex: 1 1 100%;
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
    0px 3px 1px -2px rgba(0, 0, 0, 0.12);
  align-items: center;
  justify-content: center;
  background: ${props => props.bg};
  cursor: pointer;
  opacity: ${props => (props.disabled ? '0.5' : 1)};
`;

const Wrapper = styled.div`
  display: flex;
  position: fixed;
  bottom: 86px;
  width: 100%;
  gap: 8px;
  left: 0px;
  padding: 7px 28px 6px;
  background: rgba(211, 231, 247, 1);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);

  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;

  &.hidden {
    transform: translateY(100%);
    opacity: 0;
  }

  &.visible {
    transform: translateY(0);
    opacity: 1;
  }
`;

function hasNotesMessage(
  values: Record<string, Section> & { inspector_notes?: SectionNotes; is_guaranteed?: boolean }
): boolean {
  const { inspector_notes, vehicle, is_guaranteed, ...sections } = values;
  if (inspector_notes && inspector_notes.message) return true;
  return Object.values(sections).some(section => {
    const hasOwnNotes = !!section.notes?.message?.trim();
    const hasImageNotes = section.images?.some(img => !!img.notes?.message?.trim());
    const hasCheckboxNotes = section.checkboxes?.some(cb => !!cb.notes?.message?.trim());
    const hasSubsectionNotes = section.subsections && hasNotesMessage(section.subsections);

    return hasOwnNotes || hasSubsectionNotes || hasImageNotes || hasCheckboxNotes;
  });
}

export const useActivate = () => {
  const [activateVehicle] = useActivateVehicleMutation();
  const { id: vehicleId } = useParams<{ id: string }>();
  const { data: vehicle } = useGetVehicleByIdQuery(Number(vehicleId) || skipToken);

  const { data: auctions } = useGetActiveAuctionsQuery();

  const user = useCurrentUser();

  const listedAuctionActive =
    auctions && vehicle && !!vehicle.auction_type && !!auctions[vehicle.auction_type as keyof typeof auctions];

  const history = useHistory();

  const handleList = async () => {
    if (!listedAuctionActive) return;
    const response = await activateVehicle(Number(vehicleId));
    if ('error' in response) {
      toast.error('Failed to list vehicle.');
    } else {
      toast.success('Vehicle listed.');
      const redirectUrl = user.role === 'seller' ? '/seller/vehicles' : '/admin/vehicles';
      history.push(redirectUrl);
    }
  };

  const shouldRender = vehicle?.status === VEHICLE_STATUSES.DRAFT;

  return { handleList, listedAuctionActive, shouldRender };
};

export const ActivateButton = () => {
  const isOnline = useIsOnline();

  const { input: { value: inspectorNotes } } = useField(`inspector_notes.message`)

  const { handleList, listedAuctionActive, shouldRender } = useActivate();

  useHideOnScroll('submit-btn', { enabled: shouldRender });

  const openFinalReview = useOpenFinalReview()

  const dispatch = useDispatch();

  const form = useFormState();

  const { hasAnotherEditor } = useVehicleEditing();

  const hasAtLeastComment = useMemo(() => {
    const values = form.values || {};
    const fields = ['under_hood', 'interior_questions', 'exterior', 'drive_cycles', 'inspector_notes'];

    const sections = fields.reduce((acc, curr) => {
      if (!values[curr]) return acc;
      return { ...acc, [curr]: values[curr] };
    }, {});

    console.log(sections, 'has notes message initial');
    return hasNotesMessage(sections);
  }, [form.values]);

  const finalReviewButton = !hasAtLeastComment;

  if (!shouldRender) return null;

  return (
    <Wrapper id="submit-btn">
      <FinalReviewButton
        onClick={openFinalReview}
        variant="contained"
        color="primary"
        type="button"
        bg={finalReviewButton ? '#CCCCCC' : '#424242'}
      >
        {'Final Review'}
      </FinalReviewButton>
      <ActivateButtonUI
        disabled={!listedAuctionActive || !isOnline || hasAnotherEditor || !inspectorNotes}
        onClick={handleList}
        variant="contained"
        color="primary"
        type="button"
      >
        {'Submit Vehicle'}
      </ActivateButtonUI>
    </Wrapper>
  );
};
